import { Component, inject, input, output, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { CancellationFlowSource } from 'src/app/_enums/cancellation/cancellation-flow-source.enum';
import {
  CancellationSendMethod,
  getCancellationSendMethodLabel,
  SELECTABLE_CANCELLATION_SEND_METHODS,
} from 'src/app/_enums/cancellation/cancellation-send-method.enum';
import { CustomerAccountType } from 'src/app/_enums/customer-account-type.enum';
import {
  BUSINESS_INSURANCES,
  getInsuranceTypeLabel,
  InsuranceType,
  PERSONAL_INSURANCES,
  sortInsuranceTypesByLabel,
} from 'src/app/_enums/insurance-type.enum';
import { getApiErrorMessage } from 'src/app/_helpers/api-error.helpers';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';
import { CategoryProduct } from 'src/app/_types/product.types';
import { SlideInModalComponent } from 'src/app/shared/components/slide-in-modal/slide-in-modal.component';

/**
 * « Modifier la demande »: corrects what an operator filed — the branch, the
 * insurer, the policy number, how the letter will travel, the cancellation date
 * and the comment.
 *
 * What a linked affaire dictates is shown disabled with the reason: the branch
 * once either flow is linked, the policy number and the insurer once the
 * cancelled policy is. A field the request never named is never locked, so a
 * cancellation that arrived without a branch or an insurer can be completed —
 * and until it is, nothing about it can move forward. The client and the
 * insured member are read-only: a cancellation about someone else is a
 * different cancellation.
 *
 * The route answers with the refreshed cancellation, handed up as `changed` so
 * the host repaints from one response.
 */
@Component({
  selector: 'app-cancellation-edit-modal',
  standalone: true,
  imports: [ReactiveFormsModule, SlideInModalComponent],
  templateUrl: './cancellation-edit-modal.component.html',
  styleUrl: './cancellation-edit-modal.component.css',
})
export class CancellationEditModalComponent {
  private readonly fb = inject(FormBuilder);

  private readonly cancellationService = inject(CancellationService);

  private readonly userAuthService = inject(UserAuthService);

  private readonly toastr = inject(ToastrService);

  /** How many slide-ins this one opens above. */
  readonly layer = input(0);

  /** The route landed: the refreshed cancellation. */
  readonly changed = output<CancellationFlowDetailsResponse>();

  @ViewChild('modal')
  private readonly modal?: SlideInModalComponent;

  protected cancellation: CancellationFlowDetailsResponse | null = null;

  protected readonly form = this.fb.group({
    insuranceType: new FormControl<InsuranceType | null>(
      null,
      Validators.required
    ),
    categoryId: new FormControl('', Validators.required),
    policyNumber: new FormControl('', Validators.required),
    sendMethod: new FormControl<CancellationSendMethod | null>(null),
    cancellationDate: new FormControl('', { nonNullable: true }),
    comment: new FormControl('', { nonNullable: true }),
  });

  /** The branches offered, narrowed to what a customer of this kind can hold. */
  protected insuranceTypes: InsuranceType[] = [];

  /** The insurers offering the chosen branch; empty until one is chosen. */
  protected companies: CategoryProduct[] = [];

  protected submitting = false;

  protected readonly sendMethods = SELECTABLE_CANCELLATION_SEND_METHODS;

  protected readonly getCancellationSendMethodLabel =
    getCancellationSendMethodLabel;

  protected readonly getInsuranceTypeLabel = getInsuranceTypeLabel;

  private readonly subscriptions = new Subscription();

  constructor() {
    // A company offers the branch only for some insurances, so the insurer list
    // is meaningless until the branch is known, and stale as soon as it changes.
    // Seeding rides on this too: the reset writes the branch first, so the
    // insurer it clears here is written back right after, with its list on the way.
    this.subscriptions.add(
      this.form.controls.insuranceType.valueChanges.subscribe(
        (insuranceType) => {
          this.form.controls.categoryId.setValue('');
          this.companies = [];
          if (insuranceType) this.fetchCompanies(insuranceType);
        }
      )
    );
  }

  openModal(cancellation: CancellationFlowDetailsResponse): void {
    this.cancellation = cancellation;
    this.submitting = false;
    this.seedForm(cancellation);
    this.modal?.openModal();
  }

  closeModal(): void {
    this.modal?.closeModal(false);
  }

  /** The branch cannot change once an affaire — either of them — names it. */
  protected get branchLocked(): boolean {
    const { cancellation } = this;
    if (!cancellation?.insuranceType) return false;
    return (
      !!cancellation.baseInsuranceFlow ||
      !!cancellation.replacementInsuranceFlow ||
      this.customerFiled
    );
  }

  /** The policy's own flow carries its number and its insurer… */
  private get policyLinked(): boolean {
    return !!this.cancellation?.baseInsuranceFlow;
  }

  /**
   * …and so does the customer, for whatever they filled in themselves: what they
   * asked for is read back, not written over.
   */
  private get customerFiled(): boolean {
    return this.cancellation?.source === CancellationFlowSource.CUSTOMER;
  }

  /** …unless the request never named one, which is exactly what an operator completes. */
  protected get policyNumberLocked(): boolean {
    return (
      (this.policyLinked || this.customerFiled) &&
      !!this.cancellation?.policyNumber
    );
  }

  protected get insurerLocked(): boolean {
    return (
      (this.policyLinked || this.customerFiled) &&
      !!this.cancellation?.insurer.categoryId
    );
  }

  protected get canSubmit(): boolean {
    return !this.submitting && this.form.valid;
  }

  protected submit(): void {
    if (!this.canSubmit || !this.cancellation) return;
    const value = this.form.getRawValue();
    this.submitting = true;
    this.subscriptions.add(
      this.cancellationService
        .updateCancellationFlow(this.cancellation.id, {
          insuranceType: value.insuranceType,
          categoryId: value.categoryId,
          policyNumber: value.policyNumber?.trim(),
          sendMethod: value.sendMethod,
          cancellationDate: value.cancellationDate || null,
          comment: value.comment.trim(),
        })
        .subscribe({
          next: (res) => {
            this.submitting = false;
            this.toastr.success($localize`Demande de résiliation mise à jour`);
            this.changed.emit(res.data);
            this.closeModal();
          },
          error: (err) => {
            this.submitting = false;
            this.toastr.error(
              getApiErrorMessage(
                err,
                $localize`Une erreur est survenue. Veuillez réessayer`
              )
            );
          },
        })
    );
  }

  /** Seeds the form from the aggregate; the branch's own cascade fetches its insurers. */
  private seedForm(cancellation: CancellationFlowDetailsResponse): void {
    this.form.reset({
      insuranceType: cancellation.insuranceType,
      categoryId: cancellation.insurer.categoryId ?? '',
      policyNumber: cancellation.policyNumber ?? '',
      sendMethod: cancellation.sendMethod,
      cancellationDate: cancellation.cancellationDate ?? '',
      comment: cancellation.comment ?? '',
    });
    this.applyLocks();
    this.fetchInsuranceTypes(cancellation.userId);
  }

  /**
   * What the rules forbid is shown, not hidden, so the reason can be read.
   * Silently: enabling a control emits on it, and an emission on the branch is
   * what clears the insurer.
   */
  private applyLocks(): void {
    const { insuranceType, categoryId, policyNumber } = this.form.controls;
    const silent = { emitEvent: false };
    if (this.branchLocked) insuranceType.disable(silent);
    else insuranceType.enable(silent);
    if (this.insurerLocked) categoryId.disable(silent);
    else categoryId.enable(silent);
    if (this.policyNumberLocked) policyNumber.disable(silent);
    else policyNumber.enable(silent);
  }

  private fetchInsuranceTypes(userId: string): void {
    this.subscriptions.add(
      this.userAuthService
        .getCustomerDetail({ entityId: userId, entityType: 'USER_ID' })
        .subscribe({
          next: (res: { data?: { type?: CustomerAccountType } }) => {
            // Centralisations and cancellations are requests rather than
            // insurances, so neither is a branch a policy can be held under.
            this.insuranceTypes = sortInsuranceTypesByLabel(
              (res.data?.type === CustomerAccountType.COMPANY
                ? BUSINESS_INSURANCES
                : PERSONAL_INSURANCES
              ).filter((type) => type !== InsuranceType.CENTRALISATION)
            );
          },
        })
    );
  }

  private fetchCompanies(insuranceType: InsuranceType): void {
    this.subscriptions.add(
      this.userAuthService
        ._listAllCategory({
          withoutProducts: true,
          insuranceTypes: [insuranceType],
        })
        .subscribe({
          next: (res) => {
            this.companies = [...(res.data?.categoryList ?? [])].sort((x, y) =>
              this.companyLabel(x).localeCompare(this.companyLabel(y))
            );
          },
        })
    );
  }

  protected companyLabel(company: CategoryProduct): string {
    return company.categoryInFrench?.trim() || company.category;
  }
}
